import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  Checkbox,
  Chip,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { AppModal } from "global/components/AppModal";
import { RequestParticipant, RequestFile } from "models/interfaces";
import { useAppDispatch } from "redux/hooks";
import {
  setAssignExistingFields,
  updateFileInSignRequest,
} from "redux/appSlice";
import strings from "global/constants/StringConstants";
import { useAppSelector } from "hooks/use-app-select";
import { RootState } from "utils/store";
import { fetchDocumentDimensions } from "../SignRequestService";
import { isTruthy } from "helpers/methods";

interface Props {
  isDialogOpen: boolean;
  handleDialogClose: () => void;
  files: RequestFile[];
  signers: RequestParticipant[];
  assignExistingFields: { [key: string]: boolean };
}

interface FileChoice {
  signerId?: number;
  deleteAll: boolean;
}

const truncateFileName = (name: string, maxLength = 35) => {
  if (!name || name.length <= maxLength) return name;
  const extIndex = name.lastIndexOf(".");
  if (extIndex === -1) return name.substring(0, maxLength - 3) + "...";

  const ext = name.substring(extIndex);
  const charsToShow = maxLength - ext.length - 3;
  return `${name.substring(0, charsToShow)}...${ext}`;
};

const AssignExistingFieldsModal = ({
  isDialogOpen,
  handleDialogClose,
  files,
  signers,
  assignExistingFields,
}: Props) => {
  const dispatch = useAppDispatch();
  const { isRequest, signRequest } = useAppSelector(
    (state: RootState) => state.app,
  );
  const [filesWithFields, setFilesWithFields] = useState<RequestFile[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [choices, setChoices] = useState<{ [docId: string]: FileChoice }>({});
  // Dimensions fetched up-front when the modal opens, so Save doesn't block on
  // the network. Keyed by docId.
  const [prefetchedDims, setPrefetchedDims] = useState<{
    [docId: string]: any;
  }>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isDialogOpen) {
      const filtered = files.filter((file) => {
        const docId = file.unsignedDoc || file.unsignedPdf || "";
        return (
          assignExistingFields?.[docId] ||
          file.fields?.some((f) => f.existingField)
        );
      });
      setFilesWithFields(filtered);
      setCurrentIndex(0);

      const initialChoices: { [docId: string]: FileChoice } = {};
      const defaultSignerId = signers.length > 0 ? signers[0].id : undefined;

      filtered.forEach((file) => {
        const docId = file.unsignedDoc || file.unsignedPdf || "";
        initialChoices[docId] = {
          signerId: defaultSignerId,
          deleteAll: false,
        };
      });
      setChoices(initialChoices);

      // Prefetch page dimensions in the background while the user reads the
      // dialog and picks a signer. Save needs these to convert existing-field
      // coordinates; fetching them now means Save usually has them already and
      // closes instantly instead of blocking 2-3s on the request.
      filtered.forEach((file) => {
        const docId = file.unsignedDoc || file.unsignedPdf || "";
        if (isTruthy((file as any).dimensions)) return;
        fetchDocumentDimensions(
          { unsignedPdf: file.unsignedDoc || file.unsignedPdf },
          isRequest,
          signRequest?.id,
          false,
        )
          .then((dims) => {
            if (isTruthy(dims)) {
              setPrefetchedDims((prev) => ({ ...prev, [docId]: dims }));
            }
          })
          .catch(() => {
            // Ignore; Save will retry the fetch if still missing.
          });
      });
    }
  }, [isDialogOpen, files, signers, assignExistingFields]);

  const currentFile = filesWithFields[currentIndex];
  const currentDocId =
    currentFile?.unsignedDoc || currentFile?.unsignedPdf || "";
  const currentChoice = choices[currentDocId] || { deleteAll: false };

  const handleSignerChange = (signerId: number) => {
    setChoices((prev) => ({
      ...prev,
      [currentDocId]: { ...prev[currentDocId], signerId, deleteAll: false },
    }));
  };

  const handleDeleteAllChange = (checked: boolean) => {
    setChoices((prev) => ({
      ...prev,
      [currentDocId]: { ...prev[currentDocId], deleteAll: checked },
    }));
  };

  const handleNext = () => {
    if (currentIndex < filesWithFields.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const dispatchUpdatedFile = (
    file: RequestFile,
    finalFields: any[],
    dimensions?: any,
  ) => {
    const docId = file.unsignedDoc || file.unsignedPdf || "";
    const updatedFile: RequestFile = {
      ...file,
      fields: finalFields,
      ...(isTruthy(dimensions) ? { dimensions } : {}),
    };

    dispatch(updateFileInSignRequest(updatedFile));
    dispatch(
      setAssignExistingFields({
        fileName: docId,
        fieldsExist: false,
      }),
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    for (const file of filesWithFields) {
      const docId = file.unsignedDoc || file.unsignedPdf || "";
      const choice = choices[docId];

      const nonExistingFields = (file.fields || []).filter(
        (f) => !f.existingField,
      );
      let updatedExistingFields: any[] = [];
      let fileDimensions: any =
        (file as any).dimensions || prefetchedDims[docId];

      if (choice && !choice.deleteAll) {
        // Existing fields carry PDF-point coords (x/y/w/h) that must be
        // converted into the percentage coords the editor renders with. That
        // conversion needs the page dimensions; if they haven't loaded yet the
        // math below collapses every field to 0/0 and the fields all pile up in
        // the top-left corner. Fetch the dimensions first when they're missing.
        if (!isTruthy(fileDimensions)) {
          try {
            fileDimensions = await fetchDocumentDimensions(
              { unsignedPdf: file.unsignedDoc || file.unsignedPdf },
              isRequest,
              signRequest?.id,
              false,
            );
          } catch (error) {
            // Leave undefined; the conversion keeps the field's prior values.
          }
        }

        const typeCount: Record<string, number> = {};
        const signer =
          signers.find((s) => s.id === choice.signerId) || signers[0];
        const baseName = signer?.name || signer?.requestRole || "Signer";
        const firstName = baseName.includes(" ")
          ? baseName.split(" ")[0]
          : baseName;

        nonExistingFields.forEach((f) => {
          if (f.signerIndex === choice.signerId) {
            const t = f.type || "text";
            typeCount[t] = (typeCount[t] || 0) + 1;
          }
        });
        updatedExistingFields = (file.fields || [])
          .filter((f) => f.existingField)
          .map((f) => {
            const dims = fileDimensions?.[f.page];
            const fieldPageX =
              f.fieldPageX !== undefined && f.fieldPageX !== 0
                ? f.fieldPageX
                : dims?.x
                  ? (f.x / dims.x) * 100
                  : 0;
            const fieldPageY =
              f.fieldPageY !== undefined && f.fieldPageY !== 0
                ? f.fieldPageY
                : dims?.y
                  ? (f.y / dims.y) * 100
                  : 0;
            const fieldPageWidth =
              f.fieldPageWidth !== undefined && f.fieldPageWidth !== 0
                ? f.fieldPageWidth
                : dims?.x
                  ? (f.w / dims.x) * 100
                  : 0;
            const fieldPageHeight =
              f.fieldPageHeight !== undefined && f.fieldPageHeight !== 0
                ? f.fieldPageHeight
                : dims?.y
                  ? (f.h / dims.y) * 100
                  : 0;

            const type = f.type || "text";
            typeCount[type] = (typeCount[type] || 0) + 1;
            const labelSuffix = typeCount[type];
            const isRadio = type === strings.radioButton || type === "radio";
            const generatedLabel = isRadio
              ? `Option 1`
              : `${firstName}-${type?.split(" ").join("_").toLowerCase()}-${labelSuffix}`;

            return {
              ...f,
              label: f.label || generatedLabel,
              existingField: false,
              signerIndex:
                choice.signerId !== undefined ? choice.signerId : f.signerIndex,
              fieldPageX,
              fieldPageY,
              fieldPageWidth,
              fieldPageHeight,
            };
          });
      }

      const finalFields = [...nonExistingFields, ...updatedExistingFields];
      dispatchUpdatedFile(file, finalFields, fileDimensions);
    }

    setIsSaving(false);
    handleDialogClose();
  };

  const handleSkipAll = () => {
    filesWithFields.forEach((file) => {
      const nonExistingFields = (file.fields || []).filter(
        (f) => !f.existingField,
      );
      dispatchUpdatedFile(file, [...nonExistingFields]);
    });
    handleDialogClose();
  };

  if (!currentFile) return null;

  const existingFieldsCount = (currentFile.fields || []).filter(
    (f) => f.existingField,
  ).length;
  const totalFiles = filesWithFields.length;

  const displayDocId = truncateFileName(currentDocId);

  const body = (
    <Box sx={{ width: "100%", textAlign: "left", mt: 2 }}>
      {/* Document Header Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "grey.50",
          p: 1.5,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "grey.200",
          mb: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            overflow: "hidden",
            flex: 1,
            minWidth: 0,
          }}
        >
          <InsertDriveFileOutlinedIcon color="primary" sx={{ flexShrink: 0 }} />
          <Tooltip
            title={
              <span style={{ wordBreak: "break-all" }}>{currentDocId}</span>
            }
            arrow
            placement="top"
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                minWidth: 0,
                display: "block",
              }}
            >
              {displayDocId}
            </Typography>
          </Tooltip>
        </Box>
        {totalFiles > 1 && (
          <Chip
            label={`File ${currentIndex + 1} of ${totalFiles}`}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 500, flexShrink: 0, ml: 2 }}
          />
        )}
      </Box>

      <Typography variant="body1" sx={{ mb: 4, color: "text.primary" }}>
        This document has <strong>{existingFieldsCount}</strong> pre-filled{" "}
        {existingFieldsCount === 1 ? "field" : "fields"}. Choose a signer to
        assign them to, or remove them if they aren't needed.
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          maxWidth: 400,
        }}
      >
        <FormControl fullWidth>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1, color: "text.secondary" }}
          >
            Assign to signer
          </Typography>
          <Select
            value={
              currentChoice.signerId !== undefined && !currentChoice.deleteAll
                ? currentChoice.signerId
                : ""
            }
            onChange={(e) => handleSignerChange(Number(e.target.value))}
            displayEmpty
            disabled={currentChoice.deleteAll}
            size="small"
            inputProps={{ "aria-label": "Assign to signer" }}
          >
            <MenuItem value="" disabled>
              Choose a signer
            </MenuItem>
            {signers.map((signer) => (
              <MenuItem key={signer.id} value={signer.id}>
                {signer.name || signer.email || `Signer ${signer.order}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: "flex", alignItems: "center", ml: -1.2 }}>
          <Checkbox
            checked={currentChoice.deleteAll}
            onChange={(e) => handleDeleteAllChange(e.target.checked)}
            color="primary"
            sx={{ "& .MuiSvgIcon-root": { fontSize: 26 } }}
          />
          <Typography
            variant="body1"
            color="text.primary"
            sx={{ cursor: "default" }}
          >
            Remove all fields from this document
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <AppModal
      open={isDialogOpen}
      onClose={handleSkipAll}
      title="Assign Pre-filled Fields"
      size="md"
      busy={isSaving}
      disableBackdropClose
      footer={
        <>
          {currentIndex > 0 && (
            <Button onClick={handlePrevious} variant="outlined" color="primary">
              Previous
            </Button>
          )}
          {currentIndex < filesWithFields.length - 1 ? (
            <Button
              onClick={handleNext}
              variant="contained"
              color="primary"
              disabled={
                !currentChoice.deleteAll && currentChoice.signerId === undefined
              }
            >
              Next
            </Button>
          ) : (
            <Button
              onClick={handleSave}
              variant="contained"
              color="primary"
              disabled={
                isSaving ||
                (!currentChoice.deleteAll &&
                  currentChoice.signerId === undefined)
              }
              startIcon={
                isSaving ? (
                  <CircularProgress size={15} thickness={5} color="inherit" />
                ) : undefined
              }
            >
              {isSaving ? "Saving..." : "Save"}
            </Button>
          )}
        </>
      }
    >
      {body}
    </AppModal>
  );
};

export default AssignExistingFieldsModal;
